import type { CprBoundaryModel } from 'utils/CPRUtilities/cprBoundaryUtils';

type PathPoint = { x: number; y: number };
type BoundaryEditTarget = 'lumen' | 'plaque';
type BoundarySide = 'left' | 'right';
type BoundaryScreenAxis = {
  ux: number;
  uy: number;
  pxPerCol: number;
};
type BoundaryScreenHit = {
  target: BoundaryEditTarget;
  side: BoundarySide;
  index: number;
  distance2: number;
};
type BoundarySculptSession = {
  axisCache: Partial<
    Record<
      BoundaryEditTarget,
      Partial<Record<BoundarySide, Map<number, BoundaryScreenAxis>>>
    >
  >;
};

export const findBoundaryScreenHit = ({
  center,
  radiusPx,
  targetOrder,
  canEditBoundaryTarget,
  getBoundarySideScreenPoints,
}: {
  center: PathPoint;
  radiusPx: number;
  targetOrder: BoundaryEditTarget[];
  canEditBoundaryTarget: (target: BoundaryEditTarget) => boolean;
  getBoundarySideScreenPoints: (
    target: BoundaryEditTarget,
    side: BoundarySide,
  ) => PathPoint[];
}): BoundaryScreenHit | null => {
  const radius2 = radiusPx * radiusPx;

  for (const target of targetOrder) {
    if (!canEditBoundaryTarget(target)) continue;
    let best: BoundaryScreenHit | null = null;

    for (const side of ['left', 'right'] as BoundarySide[]) {
      const points = getBoundarySideScreenPoints(target, side);
      for (let i = 0; i < points.length; i += 1) {
        const dx = points[i].x - center.x;
        const dy = points[i].y - center.y;
        const distance2 = dx * dx + dy * dy;
        if (distance2 > radius2) continue;
        if (!best || distance2 < best.distance2) {
          best = { target, side, index: i, distance2 };
        }
      }
    }

    if (best) return best;
  }

  return null;
};

export const getBoundarySculptAxis = ({
  session,
  target,
  side,
  model,
  row,
  getScreenAxisForBoundaryRow,
}: {
  session: BoundarySculptSession;
  target: BoundaryEditTarget;
  side: BoundarySide;
  model: CprBoundaryModel;
  row: number;
  getScreenAxisForBoundaryRow: (
    model: CprBoundaryModel,
    side: BoundarySide,
    row: number,
  ) => BoundaryScreenAxis | null;
}) => {
  const targetCache = (session.axisCache[target] ??= {});
  const sideCache = (targetCache[side] ??= new Map());
  const cached = sideCache.get(row);
  if (cached) return cached;

  const axis = getScreenAxisForBoundaryRow(model, side, row);
  if (axis) sideCache.set(row, axis);
  return axis;
};
